import {
  Button,
  MessageBar,
  MessageBarBody,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { Copy16Regular } from '@fluentui/react-icons';
import { forwardRef, useState } from 'react';
import type { DryRunResult, PolicyHistoryEntry } from '../../api/types';
import { formatDateTime, formatRelative } from '../../lib/format';
import { vt } from '../../theme';
import type { TagUsage } from './model';

const useStyles = makeStyles({
  head: { display: 'flex', alignItems: 'baseline', columnGap: '16px', marginBottom: '10px', minHeight: '28px' },
  title: { margin: 0, fontSize: '17px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  hint: { fontSize: '11.5px', color: vt.fg3 },
  copy: { marginLeft: 'auto', alignSelf: 'center' },
  code: {
    margin: 0,
    padding: '12px 14px',
    height: '182px',
    boxSizing: 'border-box',
    overflow: 'auto',
    borderRadius: '8px',
    backgroundColor: 'rgba(124,131,141,0.08)',
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '10.5px',
    lineHeight: '16px',
    color: vt.fg3,
    whiteSpace: 'pre',
  },
  // The two lines worth finding: the default drop, and the rule being edited.
  drop: { color: vt.red },
  focus: { color: vt.orange },
  warnings: { marginTop: '8px', display: 'flex', flexDirection: 'column', rowGap: '4px' },
  inset: {
    borderRadius: '8px',
    backgroundColor: vt.surface3,
    padding: '10px 14px',
    boxSizing: 'border-box',
    minHeight: '182px',
    maxHeight: '420px',
    overflowY: 'auto',
  },
  tagList: { listStyleType: 'none', margin: 0, padding: 0 },
  tagRow: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 90px 70px', alignItems: 'center', height: '24px' },
  chip: {
    justifySelf: 'start',
    height: '18px',
    lineHeight: '18px',
    paddingInline: '8px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: vt.tintNeutral,
    color: vt.fg2,
    fontSize: '10.5px',
  },
  unused: { color: vt.fg4 },
  count: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10px', color: vt.fg4 },
  rules: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10px', color: vt.fg3, textAlign: 'end' },
  noRules: { fontSize: '10px', color: vt.orange, textAlign: 'end' },
  table: { width: '100%' },
  th: { fontSize: '11.5px', color: vt.fg3, fontWeight: tokens.fontWeightRegular },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', color: vt.fg1 },
  muted: { color: vt.fg3 },
});

function lineClass(line: string, focusId: string | null, s: ReturnType<typeof useStyles>): string | undefined {
  if (/policy drop;|counter drop comment "default"/.test(line)) return s.drop;
  if (focusId && line.includes(`"${focusId}"`)) return s.focus;
  return undefined;
}

/** What the relay would run for the current draft (POST /policy/dry-run), with a copy button. */
export const CompiledPanel = forwardRef<HTMLElement, { compiled: DryRunResult | null; focusId: string | null }>(
  function CompiledPanel({ compiled, focusId }, ref) {
    const s = useStyles();
    const [copied, setCopied] = useState(false);
    const copy = async () => {
      if (!compiled) return;
      try {
        await navigator.clipboard.writeText(compiled.ruleset);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {
        // Clipboard denied (insecure origin, permissions): the text is still selectable.
      }
    };
    return (
      <section ref={ref} aria-label="Compiled for the relay">
        <div className={s.head}>
          <h2 className={s.title}>Compiled for the relay</h2>
          <span className={s.hint}>nftables{compiled && ` · draft r${compiled.revision}`}</span>
          <Tooltip content={copied ? 'Copied' : 'Copy the ruleset'} relationship="label">
            <Button
              className={s.copy}
              appearance="subtle"
              size="small"
              shape="circular"
              icon={<Copy16Regular />}
              disabled={!compiled}
              onClick={() => void copy()}
            />
          </Tooltip>
        </div>
        <pre className={s.code} data-testid="ruleset" tabIndex={0}>
          {compiled
            ? compiled.ruleset.split('\n').map((line, i) => (
                <span key={i} className={lineClass(line, focusId, s)}>
                  {line}
                  {'\n'}
                </span>
              ))
            : 'Compiling…'}
        </pre>
        {compiled && compiled.warnings.length > 0 && (
          <div className={s.warnings}>
            {compiled.warnings.map((w) => (
              <MessageBar key={w} intent="warning">
                <MessageBarBody>{w}</MessageBarBody>
              </MessageBar>
            ))}
          </div>
        )}
      </section>
    );
  },
);

/** The tags the policy is written in: devices carrying each, rules naming each; unused ones flagged. */
export function TagsPanel({ vocabulary }: { vocabulary: TagUsage[] }) {
  const s = useStyles();
  return (
    <section aria-label="Tags">
      <div className={s.head}>
        <h2 className={s.title}>Tags</h2>
        <span className={s.hint}>the vocabulary rules are written in</span>
      </div>
      <div className={s.inset}>
        {vocabulary.length === 0 ? (
          <span className={s.hint}>No tags yet. Tag devices on the Devices screen.</span>
        ) : (
          <ul className={s.tagList}>
            {vocabulary.map((t) => (
              <li key={t.tag} className={s.tagRow} data-tag={t.tag}>
                <span className={mergeClasses(s.chip, t.rules === 0 && s.unused)}>tag:{t.tag}</span>
                <span className={s.count}>
                  {t.devices} device{t.devices === 1 ? '' : 's'}
                </span>
                {t.rules === 0 ? (
                  <span className={s.noRules}>no rules</span>
                ) : (
                  <span className={s.rules}>
                    {t.rules} rule{t.rules === 1 ? '' : 's'}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/** Published versions, newest first. */
export function HistoryTable({ versions, activeVersion }: { versions: PolicyHistoryEntry[]; activeVersion: number | null }) {
  const s = useStyles();
  if (versions.length === 0) return <p className={s.hint}>Nothing published yet: the relay runs the open policy (v0).</p>;
  return (
    <Table className={s.table} size="small" aria-label="Policy history">
      <TableHeader>
        <TableRow>
          {['Version', 'Published', 'By', 'Summary', 'Rules'].map((h) => (
            <TableHeaderCell key={h} className={s.th}>
              {h}
            </TableHeaderCell>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {versions.map((v) => (
          <TableRow key={v.version} data-version={v.version}>
            <TableCell className={s.mono}>
              v{v.version}
              {v.version === activeVersion && <span className={s.muted}> · active</span>}
            </TableCell>
            <TableCell title={formatDateTime(v.publishedAt)}>{formatRelative(v.publishedAt)}</TableCell>
            <TableCell>{v.publishedBy}</TableCell>
            <TableCell>{v.summary}</TableCell>
            <TableCell className={s.mono}>{v.ruleCount}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
